import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { PhotoManager } from "@/components/ui/PhotoManager";
import { PhotoParentType, type PhotoResponse } from "@/types/photos";
import { usePhotos, useDeletePhoto, usePhotoUploadWorkflow } from "@/hooks/usePhotos";
import { useUpdateOrgAppBranding } from "@/hooks/useOrgAppBranding";
import { organizationSaveErrorMessage } from "@/hooks/useOrganizations";
import {
  AppBrandingEditor,
  appBrandingColourErrors,
  type AppBrandingColourField,
  type HeaderLogoSizeControl,
  type HeaderLogoSizeState,
} from "@/components/corporate/organizations/AppBrandingEditor";
import { frameWideLogo, measureSavedWideLogo, ZOOM_MAX, type WideLogoFraming } from "@/lib/logoFraming";
import { SaveBar, SectionFrame } from "@/components/corporate/organizations/page/SectionFrame";
import { useReportDirty, useSectionDraft } from "@/components/corporate/organizations/page/useSectionDraft";
import { invalidateOrganization, putOrganization, textOrNull } from "@/components/corporate/organizations/page/orgSave";
import type { Organization } from "@/types/organizations";

interface BrandingSectionProps {
  organization: Organization;
  onDirtyChange?: (dirty: boolean) => void;
}

const SQUARE_LOGO_LABEL = "Square logo — organisation picker, sign-in and placeholders";
const WIDE_LOGO_LABEL = "Wide logo — home header (optional)";
const WIDE_LOGO_HELP =
  "Any size works — empty space around the logo is removed automatically. Use 'Header logo size' above the preview to fine-tune. To change the size later, upload the logo file again. Transparent PNG recommended.";

/** Framing waits this long after the last slider move / file pick (Save flushes it at once). */
export const FRAMING_DEBOUNCE_MS = 150;

type FramingResult = WideLogoFraming | "unavailable";

/**
 * One scheduled framing of the wide logo. `promise` resolves once with the
 * framing (or "unavailable"); awaiting it again yields the SAME File, so the
 * upload guard (File identity) recognises it on a retry. `flush` starts a
 * still-debounced framing immediately.
 */
interface FramingJob {
  source: File | string;
  zoom: number;
  promise: Promise<FramingResult>;
  flush: () => void;
}

/** Pending edits of one logo slot, as reported by its PhotoManager. */
interface LogoSlotChanges {
  current: PhotoResponse[];
  newFiles: File[];
  deletedIds: string[];
}

const NO_LOGO_CHANGES: LogoSlotChanges = { current: [], newFiles: [], deletedIds: [] };

/**
 * A slider-only resize of the saved wide logo, fixed at the first Save that
 * resolves it: delete `deletedId`, upload `file` (framed at `zoom`).
 */
interface WideLogoSwap {
  deletedId: string;
  file: File;
  zoom: number;
  trimmed: ImageData;
}

interface BrandingValues {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  employeeAppBrandingEnabled: boolean;
}

const fromOrganization = (o: Organization): BrandingValues => ({
  primaryColor: o.primaryColor || "",
  secondaryColor: o.secondaryColor || "",
  accentColor: o.accentColor || "",
  employeeAppBrandingEnabled: o.employeeAppBrandingEnabled ?? false,
});

/**
 * Settings → Branding (W3 + W4): the two logos, the brand colours and "Brand the employee app" +
 * accent. One Save, in today's order: logos → `PUT organizations/{id}` (colour keys only) →
 * `PUT organizations/{id}/app-branding`. Logo work already done is never repeated on a retry.
 * After a completed save the form starts afresh from the saved values (new logo slots, no guards).
 */
export function BrandingSection({ organization, onDirtyChange }: BrandingSectionProps) {
  const [generation, setGeneration] = useState(0);
  // The values just saved, shown until the page hands over a re-read organisation.
  const [saved, setSaved] = useState<{ organization: Organization; values: BrandingValues } | null>(null);
  const initial = saved && saved.organization === organization ? saved.values : fromOrganization(organization);

  const onSaved = useCallback(
    (values: BrandingValues) => {
      setSaved({ organization, values });
      setGeneration((g) => g + 1);
    },
    [organization],
  );

  return (
    <BrandingForm
      key={generation}
      organization={organization}
      initial={initial}
      onSaved={onSaved}
      onDirtyChange={onDirtyChange}
    />
  );
}

interface BrandingFormProps {
  organization: Organization;
  initial: BrandingValues;
  onSaved: (values: BrandingValues) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

function BrandingForm({ organization, initial, onSaved, onDirtyChange }: BrandingFormProps) {
  const queryClient = useQueryClient();
  const draft = useSectionDraft(initial);
  const { values } = draft;
  const { photos } = usePhotos(PhotoParentType.Organization, organization.id);
  const { photos: widePhotos } = usePhotos(PhotoParentType.OrganizationWideLogo, organization.id);
  const deletePhoto = useDeletePhoto();
  const { uploadPhotos } = usePhotoUploadWorkflow();
  // Branding follows the stored app package: only companies on the Athlyn app take it (W3).
  const usesAthlynApp = organization.usesAthlynApp === true;

  // Track photo changes per logo slot (square = Organization, wide = OrganizationWideLogo)
  const [photoChanges, setPhotoChanges] = useState<LogoSlotChanges>(NO_LOGO_CHANGES);
  const [wideLogoChanges, setWideLogoChanges] = useState<LogoSlotChanges>(NO_LOGO_CHANGES);

  // ---- Header logo size (org-header-logo-fit, D20–D26) ----------------------
  // The wide logo the slider acts on: a picked file, else the saved logo.
  const pendingWideFile = wideLogoChanges.newFiles[0] ?? null;
  const savedWide = wideLogoChanges.current[0] ?? null;
  const savedWideUrl = savedWide?.url || null;
  const sizeSource: File | string | null = pendingWideFile ?? savedWideUrl;

  // A saved logo's current size, read once per URL (null while reading).
  const [savedMeasure, setSavedMeasure] = useState<{ url: string; zoom: number | "unavailable" } | null>(null);
  useEffect(() => {
    if (!savedWideUrl) return;
    let cancelled = false;
    const done = (zoom: number | "unavailable") => {
      if (!cancelled) setSavedMeasure({ url: savedWideUrl, zoom });
    };
    measureSavedWideLogo(savedWideUrl).then(done, () => done("unavailable"));
    return () => {
      cancelled = true;
    };
  }, [savedWideUrl]);
  const savedZoom = savedMeasure && savedMeasure.url === savedWideUrl ? savedMeasure.zoom : null;

  // Slider starting value: a picked file starts at 100 %; a saved, readable logo at its measured size.
  const startZoom: number | null = pendingWideFile
    ? ZOOM_MAX
    : typeof savedZoom === "number"
      ? savedZoom
      : null;

  // The slider value belongs to the source it was moved for; a new source starts afresh.
  const [zoomState, setZoomState] = useState<{ source: File | string; value: number } | null>(null);
  const zoom = zoomState && zoomState.source === sizeSource ? zoomState.value : (startZoom ?? ZOOM_MAX);

  // What needs framing: a picked file (always — trimming applies at any size), or
  // a saved readable logo whose slider moved away from its starting value.
  const framingSource: File | string | null = pendingWideFile
    ? pendingWideFile
    : savedWideUrl && typeof savedZoom === "number" && zoom !== savedZoom
      ? savedWideUrl
      : null;
  const framingZoom = framingSource ? zoom : null;

  // A slider-only swap fixed by a Save. Once its delete has gone through, the
  // photo list refetches without the old logo and the framing job above is
  // dropped — so the swap is reused from here, not re-derived, on every retry
  // until the wide slot's save completes. The ref is what Save reads; the state
  // is set only once its delete has gone through, and keeps the half-saved swap
  // on screen instead of "Add a wide logo".
  const wideSwapRef = useRef<WideLogoSwap | null>(null);
  const [wideSwap, setWideSwap] = useState<WideLogoSwap | null>(null);
  // Shown only when the old logo has left the list and nothing was picked since.
  const strandedSwap = !pendingWideFile && !savedWide ? wideSwap : null;

  const framingJobRef = useRef<FramingJob | null>(null);
  const [framing, setFraming] = useState<{ source: File | string; zoom: number; result: FramingResult } | null>(null);

  useEffect(() => {
    if (!framingSource || framingZoom === null) {
      framingJobRef.current = null;
      return;
    }
    const source = framingSource;
    const target = framingZoom;
    let cancelled = false;
    let started = false;
    let resolve!: (result: FramingResult) => void;
    const promise = new Promise<FramingResult>((r) => (resolve = r));
    const run = () => {
      if (started) return;
      started = true;
      clearTimeout(timer);
      frameWideLogo(source, target)
        .catch((): FramingResult => "unavailable")
        .then((result) => {
          resolve(result);
          if (!cancelled) setFraming({ source, zoom: target, result });
        });
    };
    const timer = setTimeout(run, FRAMING_DEBOUNCE_MS);
    framingJobRef.current = { source, zoom: target, promise, flush: run };
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [framingSource, framingZoom]);

  // The latest finished framing of the logo being framed (shown in the preview).
  const shownFraming =
    framing && framingSource && framing.source === framingSource && framing.result !== "unavailable"
      ? framing.result
      : null;
  const framingUnavailable =
    !!framing && !!framingSource && framing.source === framingSource && framing.result === "unavailable";

  const headerLogoSizeState: HeaderLogoSizeState = strandedSwap
    ? "swap-pending"
    : !sizeSource
    ? "no-wide-logo"
    : pendingWideFile
      ? framingUnavailable
        ? "unframeable"
        : "ready"
      : savedZoom === null
        ? "measuring"
        : savedZoom === "unavailable" || framingUnavailable
          ? "saved-unreadable"
          : "ready";

  const handleZoomChange = useCallback(
    (value: number) => {
      if (sizeSource) setZoomState({ source: sizeSource, value });
    },
    [sizeSource],
  );

  const headerLogoSize: HeaderLogoSizeControl = {
    value: strandedSwap ? strandedSwap.zoom : zoom,
    onChange: handleZoomChange,
    state: headerLogoSizeState,
    trimmed: strandedSwap ? strandedSwap.trimmed : (shownFraming?.trimmed ?? null),
  };

  // Photo work already applied by this form. If the second save (app-branding)
  // fails the edits stay, and a retry must not upload the same logo twice or
  // re-delete a photo that is already gone. Shared by both logo slots: the keys
  // (File identity, photo id) are unique across slots. A completed save starts a
  // fresh form, so they never outlive it.
  const uploadedFilesRef = useRef<WeakSet<File>>(new WeakSet());
  const deletedPhotoIdsRef = useRef<Set<string>>(new Set());

  /**
   * The wide slot's changes as they will be saved, after any in-flight or
   * debounced framing has finished:
   * - picked file ⇒ the framed file in its place (the original if it can't be framed);
   * - slider-only change on the saved logo ⇒ delete it, upload the framed one
   *   (a swap whose delete already went through is reused as is, so a retry
   *   uploads the same File once and never deletes again);
   * - otherwise the slot's changes unchanged.
   */
  const resolveWideLogoChanges = async (): Promise<LogoSlotChanges> => {
    const swap = wideSwapRef.current;
    if (!pendingWideFile && swap && deletedPhotoIdsRef.current.has(swap.deletedId)) {
      return { current: wideLogoChanges.current, deletedIds: [swap.deletedId], newFiles: [swap.file] };
    }
    const job = framingJobRef.current;
    let result: FramingResult | null = null;
    if (job) {
      job.flush();
      result = await job.promise;
    }
    const framed = result && result !== "unavailable" ? result.file : null;
    if (pendingWideFile) {
      const replacement = framed && job?.source === pendingWideFile ? framed : pendingWideFile;
      return {
        ...wideLogoChanges,
        newFiles: wideLogoChanges.newFiles.map((file) => (file === pendingWideFile ? replacement : file)),
      };
    }
    if (savedWide && result && result !== "unavailable" && job?.source === savedWideUrl) {
      const fixed: WideLogoSwap = { deletedId: savedWide.id, file: result.file, zoom: job.zoom, trimmed: result.trimmed };
      wideSwapRef.current = fixed;
      return { current: wideLogoChanges.current, deletedIds: [fixed.deletedId], newFiles: [fixed.file] };
    }
    return wideLogoChanges;
  };

  // In-flight guard: set synchronously at the top of save so a double click
  // cannot start a second save while the first is still running (the photo
  // phase has no pending mutation to disable the button).
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateAppBranding = useUpdateOrgAppBranding(organization.id);

  // The colours on the partial save: the colour keys only (OP-D14); blank clears.
  const updateColours = useMutation({
    retry: false,
    mutationFn: (colours: { primaryColor: string | null; secondaryColor: string | null }) =>
      putOrganization(organization.id, colours),
    onSuccess: () => invalidateOrganization(queryClient, organization.id),
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: organizationSaveErrorMessage(error, "Failed to update organization"),
        variant: "destructive",
      });
    },
  });

  /** Applies one logo slot's pending deletes then uploads, skipping work an earlier attempt already did. */
  const saveLogoSlot = async (parentType: PhotoParentType, changes: LogoSlotChanges) => {
    for (const photoId of changes.deletedIds) {
      if (deletedPhotoIdsRef.current.has(photoId)) continue;
      await deletePhoto.mutateAsync(photoId);
      deletedPhotoIdsRef.current.add(photoId);
      if (wideSwapRef.current?.deletedId === photoId) setWideSwap(wideSwapRef.current);
    }

    const filesToUpload = changes.newFiles.filter((file) => !uploadedFilesRef.current.has(file));
    if (filesToUpload.length > 0) {
      await uploadPhotos(parentType, organization.id, filesToUpload);
      filesToUpload.forEach((file) => uploadedFilesRef.current.add(file));
    }
  };

  const logosChanged =
    photoChanges.newFiles.length > 0 ||
    photoChanges.deletedIds.length > 0 ||
    wideLogoChanges.newFiles.length > 0 ||
    wideLogoChanges.deletedIds.length > 0 ||
    framingSource !== null ||
    strandedSwap !== null;
  const dirty = draft.dirty || logosChanged;
  useReportDirty(onDirtyChange, dirty);

  const save = async () => {
    if (submittingRef.current) return;

    const colourErrors = appBrandingColourErrors({
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      accentColor: values.accentColor,
    });
    if (Object.keys(colourErrors).length > 0) {
      toast({
        title: "Validation Error",
        description: "Fix the highlighted colour fields: use a 6-digit hex colour like #1C3446.",
        variant: "destructive",
      });
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      // Any in-flight / debounced framing finishes before anything is saved.
      const wideChangesToSave = await resolveWideLogoChanges();

      // Logos first: square, then wide (each: deletions, then uploads)
      await saveLogoSlot(PhotoParentType.Organization, photoChanges);
      await saveLogoSlot(PhotoParentType.OrganizationWideLogo, wideChangesToSave);
      // The wide slot is saved: a later retry (app-branding failed) has no swap left to finish.
      wideSwapRef.current = null;
      setWideSwap(null);

      // The colours, then the app-branding fields (their own endpoint).
      await updateColours.mutateAsync({
        primaryColor: textOrNull(values.primaryColor),
        secondaryColor: textOrNull(values.secondaryColor),
      });
      await updateAppBranding.mutateAsync({
        accentColor: values.accentColor.trim() || null,
        employeeAppBrandingEnabled: values.employeeAppBrandingEnabled,
      });

      // Invalidate both logo slots' photo queries
      queryClient.invalidateQueries({ queryKey: ["/photos", PhotoParentType.Organization, organization.id] });
      queryClient.invalidateQueries({ queryKey: ["/photos", PhotoParentType.OrganizationWideLogo, organization.id] });

      toast({ title: "Branding saved", description: "The logos, colours and app branding have been saved." });
      draft.markSaved(values);
      onSaved(values);
    } catch {
      // Each step toasts its own failure; the section keeps the values.
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleBrandingColourChange = (field: AppBrandingColourField, value: string) => {
    draft.set(field, value);
  };

  const isSaving = isSubmitting || updateColours.isPending || updateAppBranding.isPending || deletePhoto.isPending;

  const handlePhotoChange = useCallback((current: PhotoResponse[], newFiles: File[], deletedIds: string[]) => {
    setPhotoChanges({ current, newFiles, deletedIds });
  }, []);

  const handleWideLogoChange = useCallback((current: PhotoResponse[], newFiles: File[], deletedIds: string[]) => {
    setWideLogoChanges({ current, newFiles, deletedIds });
  }, []);

  const discard = () => {
    draft.discard();
    // Fresh logo slots and slider: the parent re-mounts the form with the saved values.
    onSaved(draft.base);
  };

  return (
    <SectionFrame
      section="branding"
      title="Branding"
      hint="The company's logos, brand colours and whether the employee app uses them."
      footer={
        <SaveBar
          dirty={dirty}
          saving={isSaving}
          onSave={() => void save()}
          onCancel={discard}
          note="Logos first, then the colours, then the app branding."
        />
      }
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <PhotoManager
          label={SQUARE_LOGO_LABEL}
          currentPhotos={photos}
          maxPhotos={1}
          maxFileSizeMB={5}
          onChange={handlePhotoChange}
        />
        <PhotoManager
          label={WIDE_LOGO_LABEL}
          helpText={WIDE_LOGO_HELP}
          currentPhotos={widePhotos}
          maxPhotos={1}
          maxFileSizeMB={5}
          previewFit="contain"
          onChange={handleWideLogoChange}
        />
      </div>

      <AppBrandingEditor
        idPrefix="org-branding"
        primaryColor={values.primaryColor}
        secondaryColor={values.secondaryColor}
        accentColor={values.accentColor}
        employeeAppBrandingEnabled={values.employeeAppBrandingEnabled}
        usesAthlynApp={usesAthlynApp}
        onColourChange={handleBrandingColourChange}
        onEnabledChange={(enabled) => draft.set("employeeAppBrandingEnabled", enabled)}
        logoFile={photoChanges.newFiles[0] ?? null}
        logoUrl={photoChanges.current[0]?.url ?? null}
        wideLogoFile={strandedSwap?.file ?? shownFraming?.file ?? pendingWideFile}
        wideLogoUrl={wideLogoChanges.current[0]?.url ?? null}
        organizationName={organization.name}
        headerLogoSize={headerLogoSize}
        hideWarningsWhenLocked
      />
    </SectionFrame>
  );
}

export default BrandingSection;
